<template>
  <div class="main-content">
    <div class="main-table-wrapper main-content-wrapp" :class="{'modal-opened': formVisible || historyVisible}">
      <div class="main-table-block">
        <list-heading>
          <list-title>{{ $t('firmware_schedules.title') }}</list-title>
          <a-button
              v-if="getRole === 1"
              type="primary"
              class="orange-btn"
              @click="openCreate">
            {{ $t('firmware_schedules.new') }}
          </a-button>
        </list-heading>

        <div class="admin-layout__list-filters">
          <search
              :placeholderText="$t('firmware_schedules.search_placeholder')"
              @select="searchInList"></search>
          <div class="admin-layout__filters">
            <table-size-changer
                :defaultPageSize="pageSize"
                @select="setPageSize"/>
            <a-select
                style="width: 200px; margin-left: 12px;"
                size="large"
                :placeholder="$t('firmware_schedules.filter_active')"
                allowClear
                v-model="filters.is_active"
                @change="onFilterChange"
            >
              <a-select-option :value="true">{{ $t('firmware_schedules.active') }}</a-select-option>
              <a-select-option :value="false">{{ $t('firmware_schedules.inactive') }}</a-select-option>
            </a-select>
          </div>
        </div>

        <a-table
            class="main-table firmware-schedules-table"
            :loading="loading"
            :columns="columns"
            rowKey="id"
            :pagination="false"
            :data-source="list"
            :scroll="tableScroll">

          <template slot="target" slot-scope="text, record">
            <span v-if="record.target_type === 'organization'">
              <a-tag color="blue">{{ $t('firmware_schedules.target_organization') }}</a-tag>
              <span v-if="record.organization">{{ record.organization.name }}</span>
            </span>
            <span v-else>
              <a-tag color="purple">{{ $t('firmware_schedules.target_bases') }}</a-tag>
              {{ record.bases ? record.bases.length : 0 }}
            </span>
          </template>

          <template slot="window" slot-scope="text, record">
            {{ record.window_start_time }} - {{ record.window_end_time }}
            <small style="color: #999;">({{ record.timezone }})</small>
          </template>

          <template slot="interval" slot-scope="text, record">
            {{ $t('firmware_schedules.every_x_hours', { hours: record.interval_hours }) }}
          </template>

          <template slot="active" slot-scope="text, record">
            <a-switch
                :checked="record.is_active"
                @change="(checked) => toggleActive(record, checked)"/>
          </template>

          <template slot="last_run" slot-scope="text, record">
            <span v-if="record.last_run_at">
              <a-tag :color="statusColor(record.last_run_status)">{{ record.last_run_status || '-' }}</a-tag>
              <small>{{ formatDateTime(record.last_run_at) }}</small>
            </span>
            <span v-else style="color: #999;">{{ $t('firmware_schedules.never_run') }}</span>
          </template>

          <template slot="next_run" slot-scope="text, record">
            <span v-if="record.next_run_at">{{ formatDateTime(record.next_run_at) }}</span>
            <span v-else style="color: #999;">-</span>
          </template>

          <template slot="action" slot-scope="text, record">
            <div class="action-buttons">
              <a-button @click="openEdit(record)" size="small">{{ $t('basics.edit') }}</a-button>
              <a-popconfirm
                  :title="$t('firmware_schedules.confirm_run_now')"
                  @confirm="runNow(record)">
                <a-button size="small" type="primary">{{ $t('firmware_schedules.run_now') }}</a-button>
              </a-popconfirm>
              <a-button @click="openHistory(record)" size="small">{{ $t('firmware_schedules.history') }}</a-button>
              <a-popconfirm
                  :title="$t('firmware_schedules.confirm_delete')"
                  @confirm="deleteSchedule(record)">
                <a-button size="small" type="danger">{{ $t('basics.delete') }}</a-button>
              </a-popconfirm>
            </div>
          </template>
        </a-table>
      </div>

      <pagination
          @change="setPageNumber"
          :defaultPageNumber="pageNumber"
          :paginationData="pagination"/>
    </div>

    <firmware-schedule-form
        :visible="formVisible"
        :schedule="editingSchedule"
        @close="closeForm"
        @saved="onSaved"/>

    <firmware-schedule-run-history
        :visible="historyVisible"
        :schedule="historySchedule"
        @close="historyVisible = false"/>
  </div>
</template>

<script>
import _ from 'lodash';
import { formatDateTime } from '@/utils/helpers/dateFormat';
import ListTitle from '@/components/ListTitle/ListTitle';
import ListHeading from '@/components/ListHeading/ListHeading';
import Search from '@/components/Search/Search';
import TableSizeChanger from '@/components/TableSizeChanger/TableSizeChanger';
import Pagination from '@/components/Pagination/Pagination';
import FirmwareScheduleForm from './FirmwareScheduleForm.vue';
import FirmwareScheduleRunHistory from './FirmwareScheduleRunHistory.vue';
import { i18n } from '@/i18n';

const MIN_TABLE_WIDTH = 1280;

const columns = [
  { title: i18n.t('firmware_schedules.col_name'), dataIndex: 'name', key: 'name', ellipsis: true },
  { title: i18n.t('firmware_schedules.col_target'), key: 'target', scopedSlots: { customRender: 'target' }, ellipsis: true },
  { title: i18n.t('firmware_schedules.col_interval'), key: 'interval', scopedSlots: { customRender: 'interval' }, width: 110, align: 'center' },
  { title: i18n.t('firmware_schedules.col_window'), key: 'window', scopedSlots: { customRender: 'window' } },
  { title: i18n.t('firmware_schedules.col_active'), key: 'active', scopedSlots: { customRender: 'active' }, width: 70, align: 'center' },
  { title: i18n.t('firmware_schedules.col_last_run'), key: 'last_run', scopedSlots: { customRender: 'last_run' } },
  { title: i18n.t('firmware_schedules.col_next_run'), key: 'next_run', scopedSlots: { customRender: 'next_run' } },
  { dataIndex: 'action', key: 'action', scopedSlots: { customRender: 'action' }, align: 'right' }
];

export default {
  name: 'FirmwareSchedulesList',
  components: {
    ListTitle, ListHeading, Search, TableSizeChanger, Pagination,
    FirmwareScheduleForm, FirmwareScheduleRunHistory
  },
  data() {
    return {
      formatDateTime,
      loading: false,
      columns,
      filters: { is_active: undefined },
      formVisible: false,
      editingSchedule: null,
      historyVisible: false,
      historySchedule: null,
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1920
    };
  },
  computed: {
    list() {
      return this.$store.state.firmwareSchedules.list.data || [];
    },
    pagination() {
      return this.$store.state.firmwareSchedules.list.meta || {};
    },
    tableScroll() {
      return this.viewportWidth < MIN_TABLE_WIDTH
          ? { y: 'calc(100% - 40px)', x: MIN_TABLE_WIDTH }
          : { y: 'calc(100% - 40px)' };
    },
    pageSize() {
      return this.$store.getters['firmwareSchedules/getPageSize'];
    },
    pageNumber() {
      return this.$store.getters['firmwareSchedules/getPageNumber'];
    },
    getRole() {
      return parseInt(this.$store.getters['getRole']);
    }
  },
  mounted() {
    this.fetchData();
    this.onResize = () => { this.viewportWidth = window.innerWidth; };
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    if (this.onResize) {
      window.removeEventListener('resize', this.onResize);
    }
  },
  methods: {
    fetchData(extra = {}) {
      this.loading = true;
      this.$store.dispatch('firmwareSchedules/Fetch', extra)
          .finally(() => { this.loading = false; });
    },
    setPageSize(value) {
      this.$store.commit('firmwareSchedules/SET_PAGE_SIZE', value);
      this.fetchData();
    },
    setPageNumber(value) {
      this.fetchData({ page: value });
    },
    onFilterChange() {
      this.$store.commit('firmwareSchedules/SET_FILTERS', this.filters);
      this.fetchData();
    },
    searchInList: _.debounce(function (value) {
      this.$store.commit('firmwareSchedules/SET_SEARCH_VALUE', value);
      if (value.trim().length >= 3 || value === '') {
        this.fetchData({ search: value });
      }
    }, 500),
    openCreate() {
      this.editingSchedule = null;
      this.formVisible = true;
    },
    openEdit(record) {
      this.editingSchedule = { ...record };
      this.formVisible = true;
    },
    closeForm() {
      this.formVisible = false;
      this.editingSchedule = null;
    },
    onSaved() {
      this.closeForm();
      this.fetchData();
    },
    toggleActive(record, checked) {
      this.$store.dispatch('firmwareSchedules/Update', {
        id: record.id,
        data: { is_active: checked }
      }).then(() => {
        this.$message.success(this.$t('firmware_schedules.toggle_success'));
        this.fetchData();
      }).catch(() => {
        this.$message.error(this.$t('firmware_schedules.toggle_failure'));
      });
    },
    runNow(record) {
      this.$store.dispatch('firmwareSchedules/RunNow', record.id)
          .then(() => {
            this.$message.success(this.$t('firmware_schedules.run_dispatched'));
            setTimeout(() => this.fetchData(), 12000);
          })
          .catch(() => {
            this.$message.error(this.$t('firmware_schedules.run_failure'));
          });
    },
    openHistory(record) {
      this.historySchedule = record;
      this.historyVisible = true;
    },
    deleteSchedule(record) {
      this.$store.dispatch('firmwareSchedules/Delete', record.id)
          .then(() => {
            this.$message.success(this.$t('firmware_schedules.delete_success'));
            this.fetchData();
          })
          .catch(() => {
            this.$message.error(this.$t('firmware_schedules.delete_failure'));
          });
    },
    statusColor(status) {
      const map = {
        success: 'green',
        partial: 'orange',
        skipped: 'blue',
        failed: 'red'
      };
      return map[status] || 'default';
    }
  }
};
</script>

<style lang="less" scoped>
.main-table-wrapper {
  height: 100%;
}

.action-buttons {
  display: inline-flex;
  flex-wrap: nowrap;
  gap: 4px;
  white-space: nowrap;
}

.firmware-schedules-table /deep/ .ant-table-tbody > tr > td,
.firmware-schedules-table /deep/ .ant-table-thead > tr > th {
  white-space: nowrap;
}
</style>
